import readSubjectAddressFromContext from '@Application/Subjects/readSubjectAddressFromContext';
import type { OutboxItem } from '@Domain/Ports/Outbox/OutboxPort';
import buildSubjectPath from '@UI/Pages/Beneficiaries/Shared/Helpers/buildSubjectPath';
import isInterruptedRecording from '@UI/Helpers/Outbox/isInterruptedRecording';
import useOutboxItems from '@UI/Providers/PortsProvider/Hooks/OutboxRunner/useOutboxItems';
import captureInProgressSignal from '@Guidelines/Shell/Signals/captureInProgressSignal';
import { css } from '@emotion/react';
import { TriangleAlert } from 'lucide-react';
import * as React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSignalValue } from '@verclam/pulsar';
import { Button, Message } from '@verclam/ui-kit';

const messageStyle = css`
	margin: 0 auto;
	display: flex;
	width: fit-content;
`;

const contentStyle = css`
	display: flex;
	align-items: center;
	gap: 0.75rem;
	flex-wrap: wrap;
`;

const textStyle = css`
	flex: 1 1 auto;
`;

const actionButtonStyle = css`
	min-height: 44px;
`;

type InterruptedTarget = {
	readonly item: OutboxItem;
	readonly path: string;
	readonly interviewPath: string;
};

const resolveTarget = (item: OutboxItem): InterruptedTarget | null => {
	const subject = readSubjectAddressFromContext(item.context);
	const { localInterviewId } = item.context;

	if (subject === null || localInterviewId === undefined) {
		return null;
	}

	const interviewPath = `${buildSubjectPath(subject)}/local-interviews/${localInterviewId}/`;

	return { item, path: `${interviewPath}upload`, interviewPath };
};

/**
 * Warns, on every screen, that a recording was interrupted and was never handed over: a draft with
 * no capture running in any tab. Hidden on the interview's own screens and while a new interview is
 * being created, where the user is already dealing with it.
 */
const InterruptedRecordingNotice: React.FC = () => {
	const items = useOutboxItems();
	const isCaptureInProgress = useSignalValue(captureInProgressSignal);
	const location = useLocation();
	const navigate = useNavigate();

	const interruptedTargets = React.useMemo(
		() =>
			items
				.filter((item) => isInterruptedRecording(item.state, isCaptureInProgress))
				.sort((first, second) => first.createdAt - second.createdAt)
				.map(resolveTarget)
				.filter((target): target is InterruptedTarget => target !== null),
		[items, isCaptureInProgress],
	);
	const target = interruptedTargets[0] ?? null;

	const handleView = React.useCallback((): void => {
		if (target !== null) {
			navigate(target.path);
		}
	}, [navigate, target]);

	if (
		target === null ||
		location.pathname.includes(target.interviewPath) ||
		location.pathname.includes('/interviews/new/')
	) {
		return null;
	}

	const text =
		interruptedTargets.length === 1
			? 'Un enregistrement a été interrompu.'
			: `${interruptedTargets.length} enregistrements ont été interrompus.`;

	return (
		<Message
			severity="warn"
			css={messageStyle}
			content={
				<div css={contentStyle}>
					<TriangleAlert size={20} aria-hidden="true" />
					<span css={textStyle}>{text}</span>
					<Button
						type="button"
						label="Voir l'enregistrement"
						css={actionButtonStyle}
						onClick={handleView}
					/>
				</div>
			}
		/>
	);
};

export default InterruptedRecordingNotice;
